/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   PipelineStatusBadge.tsx
 *     Feature         :   191-pipeline-monitoring-platform
 *     Description     :   Canonical status / stale / stuck badges + shared formatters for
 *                         the registry-driven monitoring components (replaced the
 *                         retired per-process Feature 147/148 badges at cutover).
 *     Constitution    :   v1.18.0 | Principle XXXVII (Code Header Standards)
 *     Author          :   Peter Dobler
 *     Created         :   2026-06-11
 * =========================================================================================
 */

import React from 'react';
import type { PipelineRunStatus } from '@shared/types/monitoredPipelines';
import { formatEastern } from '@/utils/dateFormatter';

const CONFIG: Record<PipelineRunStatus, { label: string; classes: string }> = {
  Running:   { label: 'Running',   classes: 'bg-amber-100 text-amber-800 border-amber-300' },
  Succeeded: { label: 'Succeeded', classes: 'bg-green-100 text-green-800 border-green-300' },
  Failed:    { label: 'Failed',    classes: 'bg-red-100 text-red-800 border-red-300' },
  Partial:   { label: 'Partial',   classes: 'bg-orange-100 text-orange-800 border-orange-300' },
  NoOp:      { label: 'No-op',     classes: 'bg-sky-100 text-sky-800 border-sky-300' },
  Skipped:   { label: 'Skipped',   classes: 'bg-gray-100 text-gray-600 border-gray-300' },
  Abandoned: { label: 'Abandoned', classes: 'bg-rose-100 text-rose-800 border-rose-300' },
};

const UNKNOWN = { label: 'Unknown', classes: 'bg-gray-100 text-gray-700 border-gray-300' };

export function PipelineStatusBadge({
  status,
  size = 'sm',
}: {
  status: PipelineRunStatus | string;
  size?: 'sm' | 'md';
}) {
  const { label, classes } = CONFIG[status as PipelineRunStatus] ?? { ...UNKNOWN, label: String(status) };
  const sizeClasses = size === 'md' ? 'px-2.5 py-1 text-sm' : 'px-2 py-0.5 text-xs';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border font-medium ${sizeClasses} ${classes}`}
      data-testid={`pipeline-status-badge-${status}`}
    >
      {status === 'Running' && <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />}
      {label}
    </span>
  );
}

/** Display-only health condition badges — thresholds come from the registry row. */
export function HealthConditionBadge({
  kind,
  title,
}: {
  kind: 'stale' | 'stuck';
  title?: string;
}) {
  const cfg = kind === 'stale'
    ? { label: 'Stale', classes: 'bg-gray-100 text-gray-600 border-gray-300' }
    : { label: 'Stuck', classes: 'bg-orange-100 text-orange-800 border-orange-300' };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${cfg.classes}`}
      title={title}
      data-testid={`pipeline-health-badge-${kind}`}
    >
      {cfg.label}
    </span>
  );
}

// ── Shared formatters ─────────────────────────────────────────────────────────

export function formatDuration(seconds: number | null): string {
  if (seconds === null || Number.isNaN(seconds)) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : formatEastern(d);
}

export function formatNumber(n: number | null): string {
  return n === null || Number.isNaN(n) ? '—' : n.toLocaleString();
}
